// The Tasks page's Reminders and Notes cards (garageFarmServer
// routes/assistant.js). Reminders are set by the assistant and pushed to the
// phone when due; notes come from here, the assistant or a phone shortcut.
import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/auth';
import { Button, Card, Input, Pill } from '../lib/ui';
import { formatWhen } from '../lib/briefings';
import AiTag from './AiTag';

const row = { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)' };
const errorOf = (e) => e?.response?.data?.error || e?.message || 'Something went wrong';

export function RemindersCard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    api.get('/reminders').then(({ data: d }) => setData(d)).catch(e => setError(errorOf(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const cancel = async (r) => {
    if (!window.confirm(`Cancel the reminder for ${formatWhen(r.dueAt)}?\n\n${r.text}`)) return;
    try { await api.delete(`/reminders/${r._id}`); load(); } catch (e) { setError(errorOf(e)); }
  };

  if (!data || (!data.upcoming.length && !data.recent.length && !error)) return null;
  return (
    <Card style={{ marginBottom: 16 }}>
      <h3 style={{ marginTop: 0 }}>Reminders</h3>
      {error && <p className="error">{error}</p>}
      {data.upcoming.map(r => (
        <div key={r._id} style={row}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600 }}>{r.text}<AiTag record={r} /></div>
            {r.pushText !== r.text && <div className="muted" style={{ fontSize: 13 }}>Phone shows: {r.pushText}</div>}
            {r.status === 'failed' && <div style={{ fontSize: 13, color: 'var(--bad-fg)' }}>Couldn't push it: {r.lastError}</div>}
          </div>
          <span className="muted" style={{ fontSize: 13, whiteSpace: 'nowrap' }}>{formatWhen(r.dueAt)}</span>
          {r.status === 'failed' && <Pill tone="bad">Failed</Pill>}
          <Button size="sm" variant="ghost" onClick={() => cancel(r)} aria-label="Cancel reminder">✕</Button>
        </div>
      ))}
      {data.recent.length > 0 && (
        <details style={{ marginTop: 10 }}>
          <summary className="muted" style={{ cursor: 'pointer', fontSize: 13 }}>Sent in the last two weeks ({data.recent.length})</summary>
          {data.recent.map(r => (
            <div key={r._id} style={row}>
              <div style={{ flex: 1, minWidth: 0, opacity: 0.7 }}>{r.text}<AiTag record={r} /></div>
              <span className="muted" style={{ fontSize: 13, whiteSpace: 'nowrap' }}>sent {formatWhen(r.sentAt)}</span>
            </div>
          ))}
        </details>
      )}
    </Card>
  );
}

const SOURCE_LABEL = { shortcut: 'from the phone shortcut' };

export function NotesCard() {
  const [notes, setNotes] = useState(null);
  const [text, setText] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(() => {
    api.get('/notes').then(({ data }) => setNotes(data)).catch(e => setError(errorOf(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!text.trim()) return;
    try { await api.post('/notes', { text }); setText(''); load(); } catch (e) { setError(errorOf(e)); }
  };
  const toggle = async (n) => {
    try { await api.patch(`/notes/${n._id}`, { done: !n.done }); load(); } catch (e) { setError(errorOf(e)); }
  };
  const remove = async (n) => {
    if (!window.confirm('Delete this note?')) return;
    try { await api.delete(`/notes/${n._id}`); load(); } catch (e) { setError(errorOf(e)); }
  };

  if (!notes) return null;
  return (
    <Card style={{ marginBottom: 16 }}>
      <h3 style={{ marginTop: 0 }}>Notes</h3>
      {error && <p className="error">{error}</p>}
      <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
        <Input placeholder="Jot something down" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') add(); }} />
        <Button variant="primary" onClick={add} disabled={!text.trim()}>Add</Button>
      </div>
      {notes.length === 0 && <p className="muted" style={{ margin: 0 }}>No notes yet.</p>}
      {notes.map(n => (
        <div key={n._id} style={row}>
          <input type="checkbox" checked={n.done} onChange={() => toggle(n)} style={{ width: 18, height: 18, flexShrink: 0 }} aria-label="Done" />
          <div style={{ flex: 1, minWidth: 0, whiteSpace: 'pre-wrap', opacity: n.done ? 0.55 : 1, textDecoration: n.done ? 'line-through' : 'none' }}>
            {n.text}<AiTag record={n} />
            {SOURCE_LABEL[n.source] && <span className="muted" style={{ fontSize: 12, marginLeft: 6 }}>{SOURCE_LABEL[n.source]}</span>}
          </div>
          <span className="muted" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{formatWhen(n.createdAt)}</span>
          <Button size="sm" variant="ghost" onClick={() => remove(n)} aria-label="Delete note">✕</Button>
        </div>
      ))}
    </Card>
  );
}
